import api from '@/api';
import isEmpty from 'lodash/isEmpty';

import { transformDataToTree } from './libs/transformDataToTree';
import { settingsTreeParams } from './libs/formats/treeParams';
import storeRematch from '@/store';

import testData from './libs/tree.json';

const initialOrderValue = ['S', 'E', 'W'];

// eslint-disable-next-line
const wait = (ms = 0) => new Promise(resolve => setTimeout(resolve, ms));

const treeReportsModel = {
    name: 'treeReports',
    state: {
        /*
            Порядок WSE node.
        */
        order: initialOrderValue,
        /*
            Представление дерева в виде простых вложенных объектов (готовая иерархия)
        */
        tree: null,
        /*
            Элементы дерева, которые были выбраны
        */
        checkedTreeNodes: {},
        /*
            Отслеживает взаимодействие с деревом. Помогает решить, когда нужно формировать связи
            из дерева, а когда можно это опустить.
        */
        isTouchChecked: false,
        /*
            Представление дерева после обработки AntD
        */
        treeView: null,
    },
    reducers: {
        setOrder(state, payload) {
            return { ...state, order: payload };
        },
        setTree(state, payload) {
            return { ...state, tree: payload };
        },
        setCheckedTreeNodes(state, allCheckedKeys) {
            const payload = allCheckedKeys.reduce((acc, it) => Object.assign(acc, { [it]: it }), {});

            return { ...state, checkedTreeNodes: payload, isTouchChecked: true };
        },
        /*
            При установке нового дерева надо сбросить все значения, которые были с ним связанны.
            Это checkedTreeNodes и isTouchChecked.
        */
        setTreeView(state, payload) {
            return { ...state, treeView: payload, isTouchChecked: false, checkedTreeNodes: {} };
        },
        resetTree(state) {
            return { ...state, tree: null };
        },
        resetTreeView(state) {
            return { ...state, treeView: null, isTouchChecked: false, checkedTreeNodes: {} };
        },
    },
    effects: dispatch => ({
        async setOrderEffect(payload) {
            this.setOrder(payload);

            /*
                Переход в следующий тик для того, чтобы дерево успело "схлопнуться".
                Если этого не сделать, перестройка дерева будет происходит обрывисто.

                Меняем порядок -> Ждем выполнения синхронного кода -> Запускаем build дерева
            */
            await wait();

            this.buildTree();
        },
        /*
            TODO: оптимизация функции transformDataToTree
            - подумать над применением асинхронных итераций
        */
        buildTree(_, rootState) {
            const { data } = rootState.reports.matches;
            const { order } = rootState.treeReports;

            const result = transformDataToTree(data, order);
            // const result = transformDataToTree(testData, order); // TODO: for test.
            this.setTree(result);
        },
        /*
            Возвращает WSE triplets как объект.
        */
        getDataFromTree(_, rootState) {
            const checkedTreeNodes = rootState.treeReports.checkedTreeNodes;
            const treeView = rootState.treeReports.treeView;

            return formatDataFromTree(treeView, checkedTreeNodes);
        },
        async getTreeParams(_, rootState) {
            const withTree = rootState.baseFilterReport.withTree;
            const checkedTreeNodes = rootState.treeReports.checkedTreeNodes;
            const isTouchChecked = rootState.treeReports.isTouchChecked;

            const state = storeRematch.getState();

            const {
                filterReport: { reportType },
            } = state;
            const isMer = reportType[0] === 'Mer';

            const treeView = rootState.treeReports.treeView;

            if (isMer) {
                const triplets = await dispatch.treeReports.getDataFromTree();
                /*
                    Раньше WSE триплеты формировались в ручную на клиенте (matchToString(triplets))
                    Сейчас WSE триплеты сохраняются в базу, а в ответ на сохранение присылается уникальный ключ, который
                    становится значением поля tripletIDs
                */
                const filterTriplets = triplets.filter(triplet => triplet.well);
                const response = await api.reports.makeTriplets({ triplets: filterTriplets });

                const stratums = [...new Set(filterTriplets.map(item => item.stratum))];
                const elements = [...new Set(filterTriplets.map(item => item.element))];
                const wells = [...new Set(filterTriplets.map(item => item.well))];

                // Concatenate each set to string, separated by comma
                const stratumsStr = stratums.join(',');
                const elementsStr = elements.join(',');
                const wellsStr = wells.join(',');

                /*
                    Пока собираем только tripletIDs для параметров.
                */
                return settingsTreeParams({
                    tripletIDs: response.tempTableName,
                    wellIDs: wellsStr,
                    stratumIDs: stratumsStr,
                    elementIDs: elementsStr,
                });
            }

            /*
                Если withTree == false - собираем параметры с default настройками
            */
            if (!withTree) {
                return settingsTreeParams();
            }

            /*
                Это условие значит, что были выключены все элементы дерева и
                значит вычислять связи не нужно.
            */
            if (isTouchChecked && isEmpty(checkedTreeNodes)) {
                return settingsTreeParams({ tripletIDs: ' ' });
            }

            if (treeView) {
                const triplets = await dispatch.treeReports.getDataFromTree();
                /*
                    Раньше WSE триплеты формировались в ручную на клиенте (matchToString(triplets))
                    Сейчас WSE триплеты сохраняются в базу, а в ответ на сохранение присылается уникальный ключ, который
                    становится значением поля tripletIDs
                */
                const filterTriplets = triplets.filter(triplet => triplet.well);
                const response = await api.reports.makeTriplets({ triplets: filterTriplets });

                /*
                    Пока собираем только tripletIDs для параметров.
                */

                const stratums = [...new Set(filterTriplets.map(item => item.stratum))];
                const elements = [...new Set(filterTriplets.map(item => item.element))];
                const wells = [...new Set(filterTriplets.map(item => item.well))];

                // Concatenate each set to string, separated by comma
                const stratumsStr = stratums.join(',');
                const elementsStr = elements.join(',');
                const wellsStr = wells.join(',');

                /*
                    Пока собираем только tripletIDs для параметров.
                */
                return settingsTreeParams({
                    // tripletIDs: response.tempTableName,
                    wellIDs: wellsStr,
                    stratumIDs: stratumsStr,
                    elementIDs: elementsStr,
                });
                // return settingsTreeParams({ tripletIDs: response.tempTableName });
            }

            return settingsTreeParams({ tripletIDs: ' ' });
        },
    }),
};

/*
    isEmpty(keys)

    По умолчанию в дереве все checkbox активны. Antd об это не знает, а узнает только при взаимодействии с деревом.
    По этому, если keys пусты - мы выставляем условие на проверку check / uncheck сразу в true - собирая все дерево.
*/
function formatDataFromTree(array, keys) {
    const isEmptyKeys = isEmpty(keys);

    const loop = (data, match = {}) => {
        return data.reduce((acc, item) => {
            const check = isEmptyKeys || item.key in keys;

            if (item.children && check) {
                let newMatch;

                if (item.position === 'root') {
                    newMatch = match;
                } else {
                    newMatch = { ...match, [item.type]: item.id };
                }

                return [...acc, ...loop(item.children, newMatch)];
            }

            if (check) {
                return [...acc, { ...match, [item.type]: item.id }];
            } else {
                return acc;
            }
        }, []);
    };

    return loop(array);
}

/*
    Внутренние действия пока временные, поэтому очевидные.
    Изменятся, когда будет понятно, как параметры являются невалидными для построения отчета.
*/
/*
    Функция фильтрация на значения 0 в каком либо из полей.
    ! Пока выключена (22.03.23)

    const filterZeroValues = array => {
        return array.filter(item => {
            const { well, stratum, element } = item;

            if (well === 0 || stratum === 0 || element === 0) return false;
            return true;
        });
    };
*/

/*
    ! Выключена с появлением функционала записи триплетов на базу (10.04.23)

    const formatTripletValues = array => {
        return array.map(item => {
            const { well, stratum, element } = item;
            return `${well}_${stratum}_${element}`;
        });
    };
*/

/*
    ! Выключена с появлением функционала записи триплетов на базу (10.04.23)
*/
// function matchToString(data) {
/*
        Фильтрация временно отключена

        const noZeroValues = filterZeroValues(data);
    */
// const tripletValues = formatTripletValues(data);

/*
        Пробел - это валидное значение для формирования отчета.
        Если tripletValues будет пустым, надо отправить именно пробел.
    */
// return tripletValues.join(',') || ' ';
// }

export default treeReportsModel;
